"use client";

import { Search, ChartBar as BarChart3, ShieldCheck, FileText } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "DISCOVERY",
  Icon: Search,
  description: "We begin by analyzing your current OTA performance, rate parity, and digital footprint to identify hidden revenue leaks."
}, {
  number: "02",
  title: "ANALYSIS",
  Icon: BarChart3,
  description: "Our experts deep-dive into your tech stack and booking data, benchmarking your property against local market competitors."
}, {
  number: "03",
  title: "STRATEGY",
  Icon: ShieldCheck,
  description: "We develop a bespoke revenue roadmap, focusing on high-conversion OTA strategies and optimized pricing models."
}, {
  number: "04",
  title: "REPORTING",
  Icon: FileText,
  description: "You receive a comprehensive audit report with actionable insights, ensuring your hotel is positioned for maximum profitability."
}];
export default function AuditProcess() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="auditprocess" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-default text-[10px] font-medium tracking-[0.3em] uppercase text-foreground/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; OUR METHOD
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[var(--secondary)]" />
              <span className="font-default text-xs font-medium tracking-[0.25em] uppercase text-[var(--secondary)]">
                Revenue Audit Protocol
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[64px]">
              Data-driven insights
              <br />
              <span className="text-foreground/70">
                for maximum hotel growth.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-foreground/75 md:text-xl">
              At HotelRevBaBa, we believe in precision. Our revenue audit process 
              is designed to uncover untapped potential in your property's 
              performance, ensuring every room night is optimized for profit.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-foreground/60">
              No guesswork. No wasted spend. Only the rigorous application of 
              revenue management science to your unique hospitality business.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-[var(--secondary)]" />
                        <span className="h-px w-1 bg-[var(--secondary)]/60" />
                        <span className="h-px w-0.5 bg-[var(--secondary)]/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-border bg-card backdrop-blur-xl shadow-md transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[var(--secondary)] group-hover:shadow-lg">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-default text-[120px] font-extrabold leading-none tracking-tighter text-foreground/10 transition-colors duration-[400ms] group-hover:text-[var(--secondary)]" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-foreground/80 transition-colors duration-300 group-hover:text-[var(--secondary)]" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-foreground/30 transition-all duration-500 group-hover:w-16 group-hover:bg-[var(--secondary)]" />
                        <h3 className="font-default text-2xl font-bold tracking-[0.15em] uppercase text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-foreground/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--secondary)] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-default text-xs tracking-[0.25em] uppercase text-foreground/50">
              Four pillars &mdash; one goal
            </p>
            <p className="font-default italic text-sm text-foreground/60">
              Expert revenue management for modern hoteliers.
            </p>
          </div>
        </div>
      </div>
    </section>;
}